import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import favoriteService from '../services/favoriteService';
import { useAuth } from './AuthContext';

const FavoritesContext = createContext(null);

export const FavoritesProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [favoriteIds, setFavoriteIds] = useState({ listings: [], products: [] });
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [pendingKeys, setPendingKeys] = useState({});

  const listingSet = useMemo(() => new Set(favoriteIds.listings), [favoriteIds.listings]);
  const productSet = useMemo(() => new Set(favoriteIds.products), [favoriteIds.products]);

  const refreshFavoriteIds = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setFavoriteIds({ listings: [], products: [] });
      setInitialized(true);
      return { listings: [], products: [] };
    }

    setLoading(true);
    try {
      const response = await favoriteService.getIds();
      const nextIds = response?.data || { listings: [], products: [] };
      setFavoriteIds({
        listings: Array.isArray(nextIds.listings) ? nextIds.listings : [],
        products: Array.isArray(nextIds.products) ? nextIds.products : []
      });
      return nextIds;
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    refreshFavoriteIds();
  }, [refreshFavoriteIds]);

  const isFavorite = useCallback((type, targetId) => {
    if (!targetId) return false;
    if (type === 'product') return productSet.has(targetId);
    return listingSet.has(targetId);
  }, [listingSet, productSet]);

  const isPending = useCallback((type, targetId) => {
    return !!pendingKeys[`${type}:${targetId}`];
  }, [pendingKeys]);

  const mutateLocalFavorite = useCallback((type, targetId, shouldFavorite) => {
    setFavoriteIds((prev) => {
      const key = type === 'product' ? 'products' : 'listings';
      const current = new Set(prev[key]);

      if (shouldFavorite) {
        current.add(targetId);
      } else {
        current.delete(targetId);
      }

      return {
        ...prev,
        [key]: Array.from(current)
      };
    });
  }, []);

  const toggleFavorite = useCallback(async ({ type, targetId, force }) => {
    if (!isAuthenticated || !user) {
      const error = new Error('AUTH_REQUIRED');
      error.code = 'AUTH_REQUIRED';
      throw error;
    }

    const key = `${type}:${targetId}`;
    if (pendingKeys[key]) {
      return { ignored: true, isFavorite: isFavorite(type, targetId) };
    }

    const currentlyFavorite = isFavorite(type, targetId);
    const nextFavorite = typeof force === 'boolean' ? force : !currentlyFavorite;

    setPendingKeys((prev) => ({ ...prev, [key]: true }));
    mutateLocalFavorite(type, targetId, nextFavorite);

    try {
      if (nextFavorite) {
        await favoriteService.add({ type, targetId });
      } else {
        await favoriteService.remove({ type, targetId });
      }

      return { isFavorite: nextFavorite };
    } catch (error) {
      mutateLocalFavorite(type, targetId, currentlyFavorite);
      throw error;
    } finally {
      setPendingKeys((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }, [isAuthenticated, user, pendingKeys, isFavorite, mutateLocalFavorite]);

  const value = useMemo(() => ({
    favoriteIds,
    loading,
    initialized,
    refreshFavoriteIds,
    isFavorite,
    isPending,
    toggleFavorite,
    listFavorites: favoriteService.list
  }), [favoriteIds, loading, initialized, refreshFavoriteIds, isFavorite, isPending, toggleFavorite]);

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);

  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }

  return context;
};

export default FavoritesContext;